import axios from "axios";
import Vue from 'vue';
import {VUE_APP_API_URL, ENCRYPTION_KEY} from '@/config/config';
import router from '@/routes';
import CryptoJS from 'crypto-js';

const _axios = axios.create({
    baseURL: VUE_APP_API_URL,
    timeout: 300000, headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

// Fonction pour chiffrer les données sensibles
const encryptData = (data) => {
    return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
};

// Fonction pour déchiffrer les données
const decryptData = (encryptedData) => {
    try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (error) {
        return null;
    }
};

let isReconnecting = false;
let sessionRedirigee = false;

const jetonExpire = (token) => {
    try {
        const part = token.split('.')[1];
        if (!part) return false;
        const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
        return payload.exp && payload.exp * 1000 <= Date.now();
    } catch (error) {
        return false;
    }
};

const versConnexion = () => {
    if (sessionRedirigee) return;
    const chemin = router.currentRoute && router.currentRoute.path;
    if (chemin && chemin.startsWith('/auth')) return;
    sessionRedirigee = true;
    localStorage.removeItem('user');
    localStorage.removeItem('userCredentials');
    localStorage.removeItem('rememberMe');
    Vue.$cookies.remove('OECCAtoken');
    if (Vue.$toast && Vue.$toast.open) {
        Vue.$toast.open({
            type: 'warning',
            message: 'Votre session a expiré. Veuillez vous reconnecter.',
            position: 'top-right'
        });
    }
    router.replace('/auth').catch(() => {});
};

_axios.interceptors.request.use(config => {
    const auth_token = Vue.$cookies.get('OECCAtoken');
    const url = config.url || '';
    if (auth_token && jetonExpire(auth_token) && !url.includes('/auth/signin')) {
        versConnexion();
        return Promise.reject(new Error('Session expirée'));
    }
    if (auth_token) config.headers.Authorization = `Bearer ${auth_token}`;
    return config;
}, error => Promise.reject(error));

_axios.interceptors.response.use(
    (response) => {
        const url = response.config?.url || '';
        if (url.includes('/auth/signin')) sessionRedirigee = false;
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        const statut = error.response?.status;
        const url = originalRequest?.url || '';

        if (statut === 401 && !url.includes('/auth/signin')) {
            versConnexion();
            return Promise.reject(error);
        }

        // Si le token est expiré (403) et qu'on n'a pas déjà tenté de reconnecter
        if (error.response?.status === 403 && !originalRequest._retry && !isReconnecting) {
            originalRequest._retry = true;
            isReconnecting = true;

            try {
                // Vérifier si l'utilisateur a coché "Se souvenir de moi"
                const rememberMe = localStorage.getItem('rememberMe');
                const encryptedCredentials = localStorage.getItem('userCredentials');
                
                if (rememberMe === 'true' && encryptedCredentials) {
                    const credentials = decryptData(encryptedCredentials);
                    
                    if (credentials && credentials.username && credentials.password) {
                        // Tentative de reconnexion automatique
                        const response = await _axios.post('/api/auth/signin', {
                            username: credentials.username,
                            password: credentials.password
                        });

                        const { token, user } = response.data;
                        
                        // Mettre à jour les tokens
                        Vue.$cookies.set('OECCAtoken', token, '60MIN');
                        localStorage.setItem('user', JSON.stringify(response.data));

                        // Retry la requête originale
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return _axios(originalRequest);
                    }
                }
                
                throw new Error('No valid credentials for auto-reconnection');

            } catch (reconnectError) {
                // Échec de la reconnexion, nettoyer et rediriger
                localStorage.removeItem('user');
                localStorage.removeItem('userCredentials');
                localStorage.removeItem('rememberMe');
                Vue.$cookies.remove('OECCAtoken');
                
                // Redirection vers la page de connexion
                if (router.currentRoute.path !== '/auth') {
                    router.push('/auth');
                }
                
                return Promise.reject(reconnectError);
            } finally {
                isReconnecting = false;
            }
        }

        return Promise.reject(error);
    }
);

/* _axios.interceptors.response.use(config => {
    console.log(config.status);
    return config;
}, error => Promise.reject(error)); */

export { encryptData, decryptData };
export default _axios;